import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { 
  Book, 
  Calendar, 
  Church, 
  Heart, 
  Music, 
  Newspaper, 
  MessageCircle, 
  PlayCircle,
  Sparkles,
  Moon,
  Sun,
  Share2,
  Download,
  Cross,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Youtube,
  Instagram,
  Facebook
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfUse from "@/pages/TermsOfUse";

// App screenshots from the actual Santo Universo app (high resolution)
const appScreenshots = [
  { src: "https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/hvakbkxg_1.webp", alt: "Bíblia" },
  { src: "https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/p0kxajol_2.webp", alt: "Liturgia" },
  { src: "https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/p8bwnf4u_3.webp", alt: "Homilia" },
  { src: "https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/cippzudw_4.webp", alt: "Meditação" },
  { src: "https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/a2h4tsoy_5.webp", alt: "Orações" },
  { src: "https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/hvdj73y2_6.webp", alt: "Assistente" },
  { src: "https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/3yvg6vvt_7.webp", alt: "Calendário" },
  { src: "https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/uqi7libv_8.webp", alt: "Catecismo" },
  { src: "https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/991blv61_9.webp", alt: "Santos" },
  { src: "https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/qznlt3ut_10.webp", alt: "Terço" },
  { src: "https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/xet96vw2_11.webp", alt: "Músicas" },
  { src: "https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/sui53t6r_12.webp", alt: "Estudos" },
  { src: "https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/59sb05th_13-1.webp", alt: "Café com Cristo" },
  { src: "https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/eaeprqoo_14-2.webp", alt: "Pílulas de Fé" },
  { src: "https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/rfjthqho_15.webp", alt: "Notícias" },
];

// Features data
const features = [
  {
    icon: Book,
    title: "Bíblia Sagrada",
    description: "Antigo e Novo Testamento completos com navegação fácil por livros e capítulos.",
    large: true
  },
  {
    icon: Church,
    title: "Liturgia Diária",
    description: "Leituras do dia atualizadas com Primeira Leitura, Salmo e Evangelho.",
    large: false
  },
  {
    icon: Heart,
    title: "Terço e Orações",
    description: "Terço do Dia em vídeo e centenas de orações organizadas por categoria.",
    large: false
  },
  {
    icon: PlayCircle,
    title: "Meditação Diária",
    description: "Vídeos de reflexão, Café com Cristo e Pílulas de Fé atualizados diariamente.",
    large: true
  },
  {
    icon: Calendar,
    title: "Santo do Dia",
    description: "Biografia completa dos santos com calendário litúrgico mensal.",
    large: false
  },
  {
    icon: Book,
    title: "Catecismo",
    description: "Todos os 2865 parágrafos com busca por palavra-chave.",
    large: false
  },
  {
    icon: Music,
    title: "Músicas Católicas",
    description: "Irmã Ana Paula, Frei Gilson, Padre Marcelo Rossi e muito mais.",
    large: false
  },
  {
    icon: Newspaper,
    title: "Notícias da Igreja",
    description: "Notícias católicas atualizadas e mensagens do Papa Leão XIV.",
    large: false
  },
  {
    icon: MessageCircle,
    title: "Assistente Espiritual",
    description: "Direção espiritual com IA baseada no Magistério da Igreja.",
    large: true
  }
];

// Special features
const specialFeatures = [
  { icon: Moon, text: "Tema claro e escuro" },
  { icon: Share2, text: "Compartilhe textos e imagens" },
  { icon: Sparkles, text: "Interface simples e intuitiva" },
  { icon: Download, text: "100% gratuito" }
];

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const PlayStoreIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
  </svg>
);

const HeroSection = () => {
  const scrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="hero-section" data-testid="hero-section">
      <div className="hero-bg">
        <img 
          src="https://images.unsplash.com/photo-1655923610570-44416c84c9b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxzdW4lMjByYXlzJTIwaW5zaWRlJTIwY2F0aGVkcmFsJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3MDQ5OTIyNHww&ixlib=rb-4.1.0&q=85"
          alt="Catedral com raios de sol"
          loading="eager"
        />
      </div>
      <div className="hero-overlay" />
      
      <div className="hero-content">
        <div className="animate-fade-up opacity-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/3wtm88gi_logo-tema-claro-atualizado.png"
            alt="Santo Universo Logo"
            className="w-32 h-32 md:w-40 md:h-40 mx-auto mb-6"
            data-testid="app-logo"
          />
        </div>
        
        <p className="animate-fade-up opacity-0 delay-100 text-sm uppercase tracking-[0.3em] text-[#800020] font-semibold mb-4">
          Seu Companheiro Católico Digital
        </p>
        
        <h1 className="animate-fade-up opacity-0 delay-200 font-serif text-5xl md:text-7xl font-bold text-[#1C1917] mb-6 leading-tight" data-testid="hero-title">
          Santo Universo
        </h1>
        
        <p className="animate-fade-up opacity-0 delay-300 text-lg md:text-xl text-[#57534E] max-w-2xl mx-auto mb-6 leading-relaxed">
          O aplicativo católico mais completo para fortalecer sua fé diariamente. 
          Bíblia, Liturgia, Terço, Orações e muito mais na palma da sua mão.
        </p>

        <p className="animate-fade-up opacity-0 delay-300 text-base md:text-lg text-[#800020] italic max-w-xl mx-auto mb-10 font-serif font-semibold">
          "Vossa palavra é lâmpada para os meus pés e luz para o meu caminho."
          <span className="block text-sm mt-1 not-italic text-[#57534E] font-normal">— Sl 118(119),105 • Bíblia Ave Maria</span>
        </p>
        
        <div className="animate-fade-up opacity-0 delay-400 flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <Button 
            onClick={() => window.open("https://play.google.com/store/apps/details?id=com.wnapp.id1770415591182", "_blank")}
            className="store-button-premium"
            data-testid="play-store-btn"
          >
            <PlayStoreIcon />
            <div className="text-left">
              <div className="text-xs opacity-80">Baixe no</div>
              <div className="text-base font-semibold">Google Play</div>
            </div>
          </Button>
          
          <Button 
            onClick={() => toast.info("Link da App Store será adicionado em breve!")}
            className="store-button-premium"
            data-testid="app-store-btn"
          >
            <AppleIcon />
            <div className="text-left">
              <div className="text-xs opacity-80">Baixe na</div>
              <div className="text-base font-semibold">App Store</div>
            </div>
          </Button>
        </div>

        <button 
          onClick={scrollToFeatures}
          className="animate-fade-up opacity-0 delay-500 flex flex-col items-center gap-2 text-[#A8A29E] hover:text-[#D4AF37] transition-colors cursor-pointer"
          data-testid="scroll-down-btn"
        >
          <span className="text-sm">Descubra mais</span>
          <ChevronDown className="w-6 h-6 animate-bounce" />
        </button>
      </div>
    </section>
  );
};

const FeaturesSection = () => (
  <section id="features" className="py-24 md:py-32 px-6 md:px-12 bg-[#FDFCF8] cross-pattern" data-testid="features-section">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-medium mb-4">
          Funcionalidades
        </p>
        <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1C1917] mb-6">
          Tudo para sua Jornada de Fé
        </h2>
        <div className="section-divider" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {features.map((feature, index) => (
          <div 
            key={index}
            className={`feature-card ${feature.large ? 'feature-card-large' : ''}`}
            data-testid={`feature-card-${index}`}
          >
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#D4AF37]/10 rounded-xl">
                <feature.icon className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-serif text-xl font-semibold text-[#1C1917] mb-2">
                  {feature.title}
                </h3>
                <p className="text-[#57534E] leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ScreenshotsSection = () => (
  <section className="py-24 md:py-32 bg-[#F5F2EB] overflow-hidden" data-testid="screenshots-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center mb-16">
        <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-medium mb-4">
          Conheça o App
        </p>
        <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1C1917] mb-6">
          Uma Experiência Única
        </h2>
        <div className="section-divider" />
      </div>

      <Carousel
        opts={{
          align: "center",
          loop: true,
        }}
        className="w-full max-w-6xl mx-auto"
        data-testid="screenshots-carousel"
      >
        <CarouselContent className="-ml-2 md:-ml-4">
          {appScreenshots.map((screenshot, index) => (
            <CarouselItem 
              key={index} 
              className="pl-2 md:pl-4 basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/5"
            >
              <div 
                className="flex flex-col items-center pb-4"
                data-testid={`screenshot-${index}`}
              >
                <div className="phone-frame float-animation" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="phone-screen w-40 md:w-44 aspect-[9/19.5]">
                    <img 
                      src={screenshot.src} 
                      alt={screenshot.alt}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
                <p className="text-center mt-4 text-sm text-[#57534E] font-medium">
                  {screenshot.alt}
                </p>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="flex justify-center gap-4 mt-8">
          <CarouselPrevious 
            className="relative inset-auto translate-y-0 bg-[#D4AF37] hover:bg-[#B59224] text-white border-none h-12 w-12"
            data-testid="carousel-prev-btn"
          />
          <CarouselNext 
            className="relative inset-auto translate-y-0 bg-[#D4AF37] hover:bg-[#B59224] text-white border-none h-12 w-12"
            data-testid="carousel-next-btn"
          />
        </div>
      </Carousel>
    </div>
  </section>
);

// Mission Banner - Highlighted Scripture
const MissionBanner = () => (
  <section className="relative py-20 md:py-28 px-6 md:px-12 overflow-hidden" data-testid="mission-banner">
    {/* Background with gradient */}
    <div className="absolute inset-0 bg-gradient-to-r from-[#800020] via-[#5c0018] to-[#800020]" />
    
    {/* Decorative cross pattern overlay */}
    <div className="absolute inset-0 opacity-10">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96">
        <Cross className="w-full h-full text-white" strokeWidth={0.5} />
      </div>
    </div>
    
    {/* Content */}
    <div className="relative z-10 max-w-4xl mx-auto text-center">
      <div className="mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm border border-[#D4AF37]/50 mb-6">
          <Cross className="w-8 h-8 text-[#D4AF37]" />
        </div>
      </div>
      
      <blockquote className="mb-8">
        <p className="font-serif text-2xl md:text-4xl text-white leading-relaxed mb-6 italic">
          "Anunciar o Evangelho não é glória para mim; é uma obrigação que se me impõe. 
          <span className="block mt-4 text-[#D4AF37] font-bold not-italic">Ai de mim, se eu não anunciar o Evangelho!"</span>
        </p>
      </blockquote>
      
      <div className="flex items-center justify-center gap-3">
        <div className="h-px w-12 bg-[#D4AF37]" />
        <p className="text-[#D4AF37] font-serif text-lg">1Cor 9,16</p>
        <div className="h-px w-12 bg-[#D4AF37]" />
      </div>
      <p className="text-white/60 text-sm mt-2">Bíblia CNBB • São Paulo aos Coríntios</p>
    </div>
  </section>
);

const SpecialFeaturesSection = () => (
  <section className="py-24 md:py-32 px-6 md:px-12 bg-[#FDFCF8]" data-testid="special-features-section">
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-16">
        <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-medium mb-4">
          Recursos Especiais
        </p>
        <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1C1917] mb-6">
          Projetado para Você
        </h2>
        <div className="section-divider" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        {specialFeatures.map((item, index) => (
          <div 
            key={index} 
            className="text-center group"
            data-testid={`special-feature-${index}`}
          >
            <div className="w-16 h-16 mx-auto mb-4 bg-[#D4AF37]/10 rounded-2xl flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
              <item.icon className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <p className="text-[#1C1917] font-medium">{item.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ThemeShowcaseSection = () => (
  <section className="py-24 md:py-32 px-6 md:px-12 bg-gradient-to-b from-[#F5F2EB] to-[#FDFCF8]" data-testid="theme-showcase-section">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-16">
        <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-medium mb-4">
          Sua Escolha
        </p>
        <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1C1917] mb-6">
          Tema Claro ou Escuro
        </h2>
        <p className="text-lg text-[#57534E] max-w-2xl mx-auto">
          Adapte o app ao seu estilo. Escolha entre o tema claro para o dia ou o tema escuro para uma experiência mais confortável à noite.
        </p>
        <div className="section-divider mt-6" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
        {/* Tema Claro */}
        <div className="relative group" data-testid="theme-light">
          <div className="absolute -inset-4 bg-gradient-to-r from-[#D4AF37]/20 to-[#F4D03F]/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="relative bg-white rounded-3xl p-4 shadow-xl border border-[#D4AF37]/10 group-hover:border-[#D4AF37]/30 transition-colors">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#FDFCF8] flex items-center justify-center border border-[#D4AF37]/20">
                <Sun className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-serif text-xl font-semibold text-[#1C1917]">Tema Claro</h3>
                <p className="text-sm text-[#57534E]">Ideal para o dia</p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/wrapbd82_01-%20Claro.jpg"
                alt="Santo Universo - Tema Claro"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* Tema Escuro */}
        <div className="relative group" data-testid="theme-dark">
          <div className="absolute -inset-4 bg-gradient-to-r from-[#1C1917]/20 to-[#292524]/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="relative bg-[#1C1917] rounded-3xl p-4 shadow-xl border border-[#D4AF37]/20 group-hover:border-[#D4AF37]/40 transition-colors">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#292524] flex items-center justify-center border border-[#D4AF37]/30">
                <Moon className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-serif text-xl font-semibold text-white">Tema Escuro</h3>
                <p className="text-sm text-[#A8A29E]">Confortável à noite</p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/et8a6eqd_02%20-%20Escuro.jpg"
                alt="Santo Universo - Tema Escuro"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const IdealForSection = () => {
  const audiences = [
    "Católicos praticantes",
    "Estudantes de teologia",
    "Catequistas e ministros",
    "Grupos de oração",
    "Quem busca paz espiritual"
  ];

  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-[#1C1917] text-white relative overflow-hidden" data-testid="ideal-for-section">
      <div className="absolute inset-0 opacity-10">
        <img 
          src="https://images.unsplash.com/photo-1768770922489-85275705175f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxzdGFpbmVkJTIwZ2xhc3MlMjB3aW5kb3clMjBhYnN0cmFjdCUyMGNvbG9yZnVsfGVufDB8fHx8MTc3MDQ5OTI0MHww&ixlib=rb-4.1.0&q=85"
          alt="Vitral decorativo"
          className="w-full h-full object-cover"
        />
      </div>
      
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-medium mb-4">
            Ideal Para
          </p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6">
            Feito para Fortalecer sua Fé
          </h2>
          <div className="section-divider" />
        </div>

        <div className="flex flex-wrap justify-center gap-4 mb-16">
          {audiences.map((audience, index) => (
            <div 
              key={index}
              className="px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-[#D4AF37]/30 hover:border-[#D4AF37] transition-colors"
              data-testid={`audience-${index}`}
            >
              <span className="text-white/90">{audience}</span>
            </div>
          ))}
        </div>

        <div className="text-center">
          <p className="text-xl md:text-2xl text-white/90 italic font-serif max-w-3xl mx-auto">
            "Ide pelo mundo inteiro e anunciai o Evangelho a toda criatura."
          </p>
          <p className="text-[#D4AF37] mt-3 text-sm">— Mc 16,15 • Bíblia CNBB</p>
        </div>
      </div>
    </section>
  );
};

// Quotes Section - Biblical passages and Saints
const QuotesSection = () => {
  const quotes = [
    {
      text: "A oração é a elevação da alma a Deus ou o pedido a Deus dos bens convenientes.",
      author: "São João Damasceno",
      role: "Doutor da Igreja • Catecismo §2559"
    },
    {
      text: "Não vos aflijais com coisa alguma, mas em todas as circunstâncias apresentai a Deus as vossas necessidades, pela oração e pela súplica, com ação de graças.",
      author: "Fl 4,6",
      role: "Bíblia CNBB"
    },
    {
      text: "A fé e a razão constituem como que as duas asas pelas quais o espírito humano se eleva para a contemplação da verdade.",
      author: "São João Paulo II",
      role: "Fides et Ratio, 1998"
    },
    {
      text: "Maria conservava todas estas coisas, meditando-as no seu coração.",
      author: "Lc 2,19",
      role: "Bíblia Ave Maria"
    },
    {
      text: "A Sagrada Escritura é a Palavra de Deus enquanto é redigida sob a moção do Espírito Santo. A Tradição transmite integralmente a Palavra de Deus.",
      author: "Catecismo §81",
      role: "Catecismo da Igreja Católica"
    },
    {
      text: "Orai sem cessar. Em todas as circunstâncias, dai graças, pois esta é a vontade de Deus a vosso respeito, em Cristo Jesus.",
      author: "1Ts 5,17-18",
      role: "Bíblia CNBB"
    }
  ];

  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-[#FDFCF8] relative" data-testid="quotes-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-medium mb-4">
            Sabedoria da Igreja
          </p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1C1917] mb-6">
            Palavras que Iluminam
          </h2>
          <div className="section-divider" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quotes.map((quote, index) => (
            <div 
              key={index}
              className="bg-[#F5F2EB] rounded-2xl p-6 border border-[#D4AF37]/10 hover:border-[#D4AF37]/30 transition-all duration-300 relative"
              data-testid={`quote-${index}`}
            >
              <div className="absolute -top-3 left-6 text-5xl text-[#D4AF37]/20 font-serif">"</div>
              <p className="text-[#1C1917] text-base leading-relaxed mb-5 relative z-10 italic">
                {quote.text}
              </p>
              <div className="flex items-center gap-3">
                <div className="w-1 h-8 bg-[#D4AF37] rounded-full"></div>
                <div>
                  <p className="font-serif font-semibold text-[#1C1917] text-sm">{quote.author}</p>
                  <p className="text-xs text-[#57534E]">{quote.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const DownloadSection = () => (
  <section className="py-24 md:py-32 px-6 md:px-12 bg-[#F5F2EB]" data-testid="download-section">
    <div className="max-w-4xl mx-auto text-center">
      <div className="mb-8">
        <Cross className="w-12 h-12 mx-auto text-[#D4AF37] mb-6" />
      </div>
      
      <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1C1917] mb-6">
        Baixe Agora o Santo Universo
      </h2>
      
      <p className="text-lg md:text-xl text-[#57534E] max-w-2xl mx-auto mb-4 leading-relaxed">
        Tenha a Igreja Católica sempre com você!
      </p>

      <p className="text-base text-[#800020] italic font-serif font-semibold max-w-xl mx-auto mb-10">
        "Eis que estou convosco todos os dias, até a consumação dos séculos."
        <span className="block text-sm mt-1 not-italic text-[#57534E] font-normal">— Mt 28,20 • Bíblia Ave Maria</span>
      </p>

      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
        <Button 
          onClick={() => window.open("https://play.google.com/store/apps/details?id=com.wnapp.id1770415591182", "_blank")}
          className="store-button-premium"
          data-testid="download-play-store-btn"
        >
          <PlayStoreIcon />
          <div className="text-left">
            <div className="text-xs opacity-80">Baixe no</div>
            <div className="text-base font-semibold">Google Play</div>
          </div>
        </Button>
        
        <Button 
          onClick={() => toast.info("Link da App Store será adicionado em breve!")}
          className="store-button-premium"
          data-testid="download-app-store-btn"
        >
          <AppleIcon />
          <div className="text-left">
            <div className="text-xs opacity-80">Baixe na</div>
            <div className="text-base font-semibold">App Store</div>
          </div>
        </Button>
      </div>

      <p className="text-sm text-[#A8A29E]">
        Que Nossa Senhora te abençoe! 🙏✝️
      </p>
    </div>
  </section>
);

const Footer = () => (
  <footer className="py-12 px-6 bg-[#1C1917] text-white" data-testid="footer">
    <div className="max-w-7xl mx-auto">
      {/* Social Links */}
      <div className="flex justify-center gap-4 mb-8">
        <a 
          href="https://www.youtube.com/@SantoUniversoApp" 
          target="_blank" 
          rel="noopener noreferrer"
          className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#FF0000]/20 hover:border-[#FF0000] border border-transparent transition-all duration-300"
          data-testid="social-youtube"
          aria-label="YouTube"
        >
          <Youtube className="w-5 h-5 text-white" />
        </a>
        <a 
          href="https://www.instagram.com/osantouniverso/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#E4405F]/20 hover:border-[#E4405F] border border-transparent transition-all duration-300"
          data-testid="social-instagram"
          aria-label="Instagram"
        >
          <Instagram className="w-5 h-5 text-white" />
        </a>
        <a 
          href="https://www.facebook.com/profile.php?id=61571888013125" 
          target="_blank" 
          rel="noopener noreferrer"
          className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#1877F2]/20 hover:border-[#1877F2] border border-transparent transition-all duration-300"
          data-testid="social-facebook"
          aria-label="Facebook"
        >
          <Facebook className="w-5 h-5 text-white" />
        </a>
        <a 
          href="https://whatsapp.com/channel/0029Vb6dT1517En1Qx5KNw0N" 
          target="_blank" 
          rel="noopener noreferrer"
          className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#25D366]/20 hover:border-[#25D366] border border-transparent transition-all duration-300"
          data-testid="social-whatsapp"
          aria-label="Canal WhatsApp"
        >
          <MessageCircle className="w-5 h-5 text-white" />
        </a>
      </div>

      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_ade5b22e-2078-466a-8931-f51bba223cfa/artifacts/3wtm88gi_logo-tema-claro-atualizado.png"
            alt="Santo Universo"
            className="w-10 h-10 rounded-lg"
          />
          <span className="font-serif text-lg font-semibold">Santo Universo</span>
        </div>
        
        <div className="flex flex-col md:flex-row items-center gap-4">
          <Link 
            to="/politica-de-privacidade" 
            className="text-[#A8A29E] hover:text-[#D4AF37] text-sm transition-colors"
            data-testid="privacy-policy-link"
          >
            Política de Privacidade
          </Link>
          <Link 
            to="/termos-de-uso" 
            className="text-[#A8A29E] hover:text-[#D4AF37] text-sm transition-colors"
            data-testid="terms-of-use-link"
          >
            Termos de Uso
          </Link>
          <p className="text-[#A8A29E] text-sm">
            © 2025 Santo Universo. Todos os direitos reservados.
          </p>
        </div>
        
        <div className="flex items-center gap-2 text-[#D4AF37]">
          <Cross className="w-4 h-4" />
          <span className="text-sm">Deus seja louvado</span>
        </div>
      </div>
    </div>
  </footer>
);

const HomePage = () => (
  <div className="App bg-[#FDFCF8]" data-testid="santo-universo-landing">
    <Toaster position="top-center" richColors />
    <HeroSection />
    <FeaturesSection />
    <MissionBanner />
    <ScreenshotsSection />
    <SpecialFeaturesSection />
    <ThemeShowcaseSection />
    <QuotesSection />
    <IdealForSection />
    <DownloadSection />
    <Footer />
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />
        <Route path="/termos-de-uso" element={<TermsOfUse />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
